import React, { Component } from "react";

import { faStar } from "@fortawesome/free-solid-svg-icons";
import { Grid } from "@material-ui/core";
import { includes } from "lodash";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import queryString from "query-string";
import styled from "styled-components/macro";

import { Label } from "components/Form/FormikControls";
import SearchInput from "components/Form/SearchInput";
import { MainContentWrapper } from "components/Layout";
import MessageBox from "components/MessageBox";
import { ReactSelect } from "components/SearchableSelector";
import { SendVendorReportsButton } from "components/SendVendorReportsModal/sendVendorReportsButton";
import Table from "components/Table";
import TabRow from "components/TabSelector/TabRow";

import {
  ALL_REPORT_TYPES,
  REPORT_DISPLAY_NAME,
  ReportType,
} from "constants/reports";
import { fontSizes, space } from "constants/theme";

import { filterAll } from "lib";

import { agentReport } from "./columns";
import { FavouritesTables } from "./ReportFavouritesTables";

const PaddedGrid = styled(Grid)`
  padding: 0 ${({ theme }) => theme.space[1]}px;
`;

const urlParam = "tab";

const defaultReportData = {
  agencies: [],
  branches: [],
  vendors: [],
  buyers: [],
  sale_files: [],
  nlis: [],
  saleyard: [],
};

const defaultFilters = {
  vendors: [],
  buyers: [],
  agency: [],
  agents: [],
};

const {
  columnsGenericFile,
  columnsAgentWithAgency,
  columnsBranches,
  columnsBuyers,
  columnsVendors,
  columnsSaleFiles,
  columnsNLIS,
  columnsAgent,
} = agentReport;

const reportTabStyle = {
  fontSize: fontSizes.alpha,
  margin: 0,
  padding: `${space[2]}px ${space[1]}px 0 ${space[1]}px`,
};

export class AgentReports extends Component {
  state = {
    selectedTab:
      queryString.parse(window.location.search)[urlParam] ||
      ReportType.FAVOURITES ||
      ReportType.AGENCIES,
    filteredData: [],
    businesses: [],
    searchTerm: "",
  };

  componentDidMount() {
    return this.updateStore();
  }

  componentDidUpdate(prevProps) {
    const { reportData = defaultReportData, reportFavourites } = this.props;
    if (!isEqual(prevProps.reportData, reportData)) {
      this.updateStore();
    }
    if (!isEqual(prevProps.reportFavourites, reportFavourites)) {
      this.handleFavourites();
    }
  }

  showAgencyFilter = () => {
    const { selectedTab } = this.state;
    return includes(
      [
        ReportType.AGENCIES,
        ReportType.SALE_FILES,
        ReportType.SALE_EXPORT,
        ReportType.STUD,
        ReportType.CARDS,
      ],
      selectedTab,
    );
  };

  showAgentFilter = () => {
    const { selectedTab } = this.state;
    return includes([ReportType.AGENTS], selectedTab);
  };

  updateStore = () => {
    const { reportData = defaultReportData, filters } = this.props;
    let { selectedTab } = this.state;
    // If selectedTab isn't in the tab list (ie agencies is default), get the
    // first one that is.  If we don't have any tabs, don't do any updates...
    // they'll come, later, probably.
    if (!reportData[selectedTab] || reportData[selectedTab].length === 0) {
      const tabs = this.getTabs();
      if (tabs.length === 0) {
        return;
      }
      selectedTab = tabs[0].id;
    }

    this.setState({
      filteredData: reportData[selectedTab],
      selectedTab,
      selectedAgency:
        selectedTab === ReportType.AGENCIES && filters[selectedTab].length > 0
          ? filters[selectedTab]?.[0]
          : "",
      selectedAgent:
        selectedTab === ReportType.AGENTS && filters[selectedTab].length > 0
          ? filters[selectedTab]?.[0]
          : "",
      selectedBusiness:
        includes(
          [
            ReportType.AGENCIES,
            ReportType.BRANCHES,
            ReportType.SALE_FILES,
            ReportType.NLIS,
            ReportType.SALE_EXPORT,
            ReportType.SALEYARD,
          ],
          selectedTab,
        ) || filters[selectedTab]?.length === 0
          ? ""
          : filters[selectedTab]?.[0].value || null,
      businesses:
        includes(
          [
            ReportType.AGENCIES,
            ReportType.BRANCHES,
            ReportType.SALE_FILES,
            ReportType.NLIS,
            ReportType.SALE_EXPORT,
            ReportType.SALEYARD,
          ],
          selectedTab,
        ) || filters[selectedTab]?.length === 0
          ? []
          : filters[selectedTab],
    });
  };

  handleFavourites = () => {
    const { selectedTab } = this.state;
    const { reportFavourites, reportData } = this.props;
    // favourites tab dissapears when no reports are favourited,
    // this is preventing users staring at a blank page
    if (selectedTab === ReportType.FAVOURITES && reportFavourites.length < 1) {
      this.setState({
        selectedTab: ReportType.AGENCIES,
        filteredData: reportData.agencies,
      });
    }
  };

  handleChangeSearch = searchTerm => {
    const { searchTerm: oldSearchTerm } = this.state;
    if (searchTerm !== oldSearchTerm) {
      this.setState(
        {
          searchTerm,
        },
        () => this.filterData(),
      );
    }
  };

  handleOnChangeFilterBusiness = option => {
    const { selectedBusiness } = this.state;
    if (option.value !== selectedBusiness.value) {
      this.setState(
        {
          selectedBusiness: option,
        },
        () => this.filterData(),
      );
    }
  };

  handleOnChangeFilterAgency = option => {
    const { selectedAgency } = this.state;
    if (option.value !== selectedAgency.value) {
      this.setState(
        {
          selectedAgency: option,
        },
        () => this.filterData(),
      );
    }
  };

  handleOnChangeFilterAgent = option => {
    const { selectedAgent } = this.state;
    if (option.value !== selectedAgent.value) {
      this.setState(
        {
          selectedAgent: option,
        },
        () => this.filterData(),
      );
    }
  };

  filterData = () => {
    const { reportData = defaultReportData } = this.props;
    const {
      searchTerm,
      selectedTab,
      selectedBusiness,
      selectedAgency,
      selectedAgent,
    } = this.state;

    // Business with id -1 is "ALL businesses"
    const selectedBusinessValue = selectedBusiness?.value || -1;
    const selectedAgencyValue = selectedAgency?.value;
    const selectedAgentValue = selectedAgent?.value;
    if (this.showAgencyFilter() && selectedAgencyValue) {
      this.setState({
        filteredData: filterAll(
          reportData[selectedTab].filter(
            row => row.agency === selectedAgencyValue,
          ),
          searchTerm,
        ),
      });
    } else if (this.showAgentFilter()) {
      // If we have picked a agent then filter the vendor and reports to show
      if (selectedAgentValue) {
        this.setState({
          filteredData: filterAll(
            reportData[selectedTab].filter(
              row =>
                row.business_name === selectedAgentValue ||
                row.related_agent === selectedAgentValue,
            ),
            searchTerm,
          ),
        });
      } else {
        // Otherwise just show the agents
        this.setState({
          filteredData: reportData[selectedTab].filter(
            row => !row?.related_agent,
          ),
        });
      }
    } else if (
      selectedBusinessValue === -1 ||
      selectedTab === ReportType.AGENCIES
    ) {
      this.setState({
        filteredData: filterAll(reportData[selectedTab], searchTerm),
      });
    } else if (
      !includes(
        [
          ReportType.BRANCHES,
          ReportType.SALE_FILES,
          ReportType.NLIS,
          ReportType.SALE_EXPORT,
        ],
        selectedTab,
      )
    ) {
      const filteredByBusiness = reportData[selectedTab].filter(
        row => row.business_id === selectedBusinessValue,
      );
      const filterBySearchTerm = filterAll(filteredByBusiness, searchTerm);
      this.setState({
        filteredData: filterBySearchTerm,
      });
    } else {
      this.setState({
        filteredData: reportData[selectedTab],
      });
    }
  };

  handleChangeTab = selected => {
    const { selectedTab } = this.state;
    const { filters = defaultFilters } = this.props;
    const url = new URL(window.location);
    url.searchParams.set(urlParam, selected);
    window.history.pushState(null, "", url);
    if (selectedTab !== selected) {
      this.setState(
        {
          selectedTab: selected,
          searchTerm: "",
          selectedAgency:
            !filters[selected] || filters[selected].length === 0
              ? ""
              : filters[selected][0],
          selectedAgent:
            !filters[selected] || filters[selected].length === 0
              ? ""
              : filters[selected][0],
          selectedBusiness:
            includes(
              [
                ReportType.AGENCIES,
                ReportType.BRANCHES,
                ReportType.SALE_FILES,
                ReportType.NLIS,
                ReportType.SALE_EXPORT,
              ],
              selected,
            ) ||
            !filters[selected] ||
            filters[selected].length === 0
              ? ""
              : filters[selected][0],
          businesses:
            includes(
              [
                ReportType.AGENCIES,
                ReportType.BRANCHES,
                ReportType.SALE_FILES,
                ReportType.NLIS,
                ReportType.SALE_EXPORT,
              ],
              selected,
            ) ||
            !filters[selected] ||
            filters[selected].length === 0
              ? []
              : filters[selected],
        },
        () => {
          this.filterData();
        },
      );
    }
  };

  selectColumns = (selectedOption, selectedAgent) => {
    if (selectedOption === ReportType.AGENCIES) {
      const { filters = defaultFilters } = this.props;
      if (filters.agencies.length > 0) {
        return columnsAgentWithAgency;
      } else {
        return columnsGenericFile;
      }
    } else if (selectedOption === ReportType.BRANCHES) {
      return columnsBranches;
    } else if (selectedOption === ReportType.SALE_FILES) {
      return columnsSaleFiles;
    } else if (selectedOption === ReportType.CARDS) {
      return columnsAgentWithAgency;
    } else if (selectedOption === ReportType.SALEYARD) {
      return columnsGenericFile;
    } else if (selectedOption === ReportType.NLIS) {
      return columnsNLIS;
    } else if (selectedOption === ReportType.BUYERS) {
      return columnsBuyers;
    } else if (selectedOption === ReportType.SALE_EXPORT) {
      return columnsNLIS;
    } else if (selectedOption === ReportType.VENDORS) {
      return columnsVendors;
    } else if (selectedOption === ReportType.STUD) {
      return columnsAgentWithAgency;
    } else if (selectedOption === ReportType.AGENTS) {
      return columnsAgent(selectedAgent);
    } else {
      return columnsGenericFile;
    }
  };

  displayTable = () => {
    const {
      filteredData,
      selectedBusiness,
      selectedAgency,
      selectedAgent,
      selectedTab,
      searchTerm,
      businesses,
    } = this.state;
    const { filters = defaultFilters } = this.props;
    // For columns that have an explicit accessor, if we dont have any keys in our
    // data for that accessor, remove the column.  This is quick workaround for
    // hiding columns that arent required for specific sale types (Clearing sales).
    // TODO: Refactor this into a functional component and do better column
    // definitons based on the sale type.
    const possibleColumns = Object.keys(
      filteredData.reduce((result, obj) => {
        return Object.assign(result, obj);
      }, {}),
    );
    const columns = this.selectColumns(selectedTab, selectedAgent).filter(
      colDef =>
        colDef.accessor ? possibleColumns.includes(colDef.accessor) : true,
    );

    const areFiltersAvailable =
      filters.agencies?.length > 0 || businesses?.length > 0;
    return (
      <>
        <PaddedGrid container spacing={2} alignItems="flex-end">
          {areFiltersAvailable && (
            <Grid item xs={12} sm={6}>
              <Label>Showing reports from</Label>
              {this.showAgencyFilter() && (
                <ReactSelect
                  value={selectedAgency}
                  options={filters.agencies}
                  onChange={this.handleOnChangeFilterAgency}
                  menuPortalTarget={document.body}
                />
              )}
              {businesses?.length > 0 && !this.showAgentFilter() && (
                <ReactSelect
                  value={selectedBusiness}
                  options={businesses}
                  onChange={this.handleOnChangeFilterBusiness}
                  menuPortalTarget={document.body}
                />
              )}
              {this.showAgentFilter() && (
                <ReactSelect
                  value={selectedAgent}
                  options={filters.agents}
                  onChange={this.handleOnChangeFilterAgent}
                  menuPortalTarget={document.body}
                />
              )}
            </Grid>
          )}
          <Grid container item xs={12} sm={6} justifyContent="flex-end">
            <SearchInput
              onChange={this.handleChangeSearch}
              value={searchTerm}
            />
          </Grid>
        </PaddedGrid>

        {selectedTab === ReportType.VENDORS && <SendVendorReportsButton />}

        {selectedTab === ReportType.FAVOURITES ? (
          <FavouritesTables searchTerm={searchTerm} />
        ) : (
          <Table data={filteredData} columns={columns} suppressNoResult />
        )}
      </>
    );
  };

  getTabs = () => {
    const { reportData = defaultReportData, reportFavourites } = this.props;
    const tabs = ALL_REPORT_TYPES.filter(tabId =>
      tabId === ReportType.FAVOURITES
        ? reportFavourites?.length
        : reportData[tabId] &&
          (reportData[tabId]?.length || reportData[`${tabId}_force_display`]),
    );

    return tabs.map(tabId => ({
      id: tabId,
      title: REPORT_DISPLAY_NAME[tabId],
      icon: tabId === ReportType.FAVOURITES ? faStar : null,
    }));
  };

  render() {
    const { selectedTab } = this.state;
    const { reportData = defaultReportData } = this.props;
    const tabs = this.getTabs();

    const displayTable =
      selectedTab !== ReportType.FAVOURITES &&
      reportData[selectedTab] &&
      reportData[selectedTab]?.length === 0 &&
      !reportData[`${selectedTab}_force_display`];

    return (
      <MainContentWrapper>
        <TabRow
          tabs={tabs}
          selectedTab={selectedTab}
          setSelectedTab={this.handleChangeTab}
          color="#666"
          style={reportTabStyle}
        />

        {displayTable ? (
          <MessageBox>
            Reports will be available once the sale is finalised and confirmed.
          </MessageBox>
        ) : (
          this.displayTable()
        )}
      </MainContentWrapper>
    );
  }
}

AgentReports.propTypes = {
  reportData: PropTypes.shape({
    agencies: PropTypes.array,
    branches: PropTypes.array,
    vendors: PropTypes.array,
    buyers: PropTypes.array,
    sale_files: PropTypes.array,
    nlis: PropTypes.array,
    saleyard: PropTypes.array,
  }),
  filters: PropTypes.shape({
    vendors: PropTypes.array,
    buyers: PropTypes.array,
    agency: PropTypes.array,
    agents: PropTypes.array,
  }),
};
